/**
 * Zone Variance Calculation
 *
 * Calculates variance metrics that measure rider consistency within zones.
 * Identifies which aspects of riding show the most variation.
 */

import {
  type ZoneVarianceMetrics,
  type ZoneVarianceDetails,
  type TrackZoneType,
  type InconsistencyType,
  type NoiseToleranceConfig,
  type ZoneDetectionConfig,
  type VarianceOutlierLap,
} from './TrackZoneTypes'
import { DEFAULT_NOISE_TOLERANCE } from '../config/zoneDetection'
import { type ZoneDataPoint } from './ZoneDetection'
import { type LapZoneMetrics, extractLapZoneMetrics } from './LapZoneMetrics'
import { average, standardDeviation } from './mathUtils'
import {
  median,
  detectSingleOutlier,
} from '../sessionAnalysis/zoneDisplay/referencePointConsistency/robustStats'
import {
  SPEED_OK_THRESHOLD_KMH,
  BRAKE_OK_THRESHOLD_M,
} from '../config/consistencyScoring'

// ============================================================================
// Main Variance Calculation
// ============================================================================

/** Calculate variance metrics for a zone with detailed breakdown */
export function calculateVarianceMetrics(
  zoneData: ZoneDataPoint[][],
  config: ZoneDetectionConfig,
  zoneType?: TrackZoneType,
  zoneStartDistance: number = 0,
  noiseTolerance: NoiseToleranceConfig = DEFAULT_NOISE_TOLERANCE,
  lapNumbers?: number[]
): ZoneVarianceMetrics {
  const lapCount = zoneData.length
  const lapMetrics = zoneData.map((lapData) =>
    extractLapZoneMetrics(lapData, zoneStartDistance)
  )

  const details = calculateVarianceDetails(
    lapMetrics,
    zoneData,
    noiseTolerance,
    lapNumbers
  )
  const primaryIssue = determinePrimaryIssue(details, zoneType)
  const primaryInconsistency = mapToLegacyInconsistency(primaryIssue)

  // Legacy metrics
  const peakLeans = zoneData.map((d) =>
    Math.max(...d.map((p) => Math.abs(p.leanAngle)))
  )
  const entryDistances = zoneData.map((d) => d[0]?.distance ?? 0)
  const exitDistances = zoneData.map((d) => d[d.length - 1]?.distance ?? 0)
  const minSpeeds = lapMetrics.map((m) => m.minSpeed)
  const speedCV =
    average(minSpeeds) > 0
      ? standardDeviation(minSpeeds) / average(minSpeeds)
      : 0

  return {
    speedCV,
    leanAngleStdDev: standardDeviation(peakLeans),
    entryDistanceStdDev: standardDeviation(entryDistances),
    exitDistanceStdDev: standardDeviation(exitDistances),
    lapCount,
    primaryInconsistency,
    primaryIssue,
    details,
  }
}

// ============================================================================
// Variance Details
// ============================================================================

function calculateVarianceDetails(
  lapMetrics: LapZoneMetrics[],
  zoneData: ZoneDataPoint[][],
  noiseTolerance: NoiseToleranceConfig,
  lapNumbers?: number[]
): ZoneVarianceDetails {
  const zoneLength = calculateZoneLength(zoneData)

  return {
    entrySpeedVariance: {
      ...calculateSpeedVariance(
        lapMetrics.map((m) => m.entrySpeed),
        noiseTolerance
      ),
      outlierLap: detectOutlierLap(
        pairMetricValues(lapMetrics, lapNumbers, (m) => m.entrySpeed),
        SPEED_OK_THRESHOLD_KMH
      ),
    },
    exitSpeedVariance: {
      ...calculateSpeedVariance(
        lapMetrics.map((m) => m.exitSpeed),
        noiseTolerance
      ),
      outlierLap: detectOutlierLap(
        pairMetricValues(lapMetrics, lapNumbers, (m) => m.exitSpeed),
        SPEED_OK_THRESHOLD_KMH
      ),
    },
    apexSpeedVariance: {
      ...calculateSpeedVariance(
        lapMetrics.map((m) => m.minSpeed),
        noiseTolerance
      ),
      outlierLap: detectOutlierLap(
        pairMetricValues(lapMetrics, lapNumbers, (m) => m.minSpeed),
        SPEED_OK_THRESHOLD_KMH
      ),
    },
    apexLocationVariance: calculateApexLocationVariance(
      lapMetrics,
      zoneLength,
      noiseTolerance
    ),
    brakingPointVariance: {
      ...calculateDistanceVariance(
        lapMetrics
          .map((m) => m.brakingPointDistance)
          .filter((d): d is number => d !== null),
        noiseTolerance
      ),
      outlierLap: detectOutlierLap(
        pairMetricValues(lapMetrics, lapNumbers, (m) => m.brakingPointDistance),
        BRAKE_OK_THRESHOLD_M
      ),
    },
    brakeReleaseVariance: calculateDistanceVariance(
      lapMetrics
        .map((m) => m.brakeReleaseDistance)
        .filter((d): d is number => d !== null),
      noiseTolerance
    ),
    brakingIntensityVariance: calculateGForceVariance(
      lapMetrics.map((m) => m.peakBrakingG),
      noiseTolerance
    ),
    throttlePointVariance: calculateThrottlePointVariance(
      lapMetrics,
      zoneLength,
      noiseTolerance
    ),
    throttleIntensityVariance: calculateGForceVariance(
      lapMetrics.map((m) => m.peakThrottleG),
      noiseTolerance
    ),
    coastingVariance: calculateCoastingVariance(
      lapMetrics.map((m) => m.coastingDistance),
      noiseTolerance
    ),
  }
}

interface MetricValuePair {
  value: number
  lapNumber: number
}

/**
 * Pair a metric's per-lap values with their lap numbers, applying the SAME
 * null-filter the aggregate range/stdDev use — attribution must run on
 * exactly the value set the quoted range came from. Returns undefined when
 * lap identity is unavailable (callers without lapNumbers stay aggregate-only).
 */
function pairMetricValues(
  lapMetrics: LapZoneMetrics[],
  lapNumbers: number[] | undefined,
  select: (m: LapZoneMetrics) => number | null
): MetricValuePair[] | undefined {
  if (!lapNumbers || lapNumbers.length !== lapMetrics.length) return undefined
  return lapMetrics
    .map((m, i) => ({ value: select(m), lapNumber: lapNumbers[i] }))
    .filter((p): p is MetricValuePair => p.value !== null)
}

/** F3 semantics via detectSingleOutlier; restWithin = literal bound of the rest. */
function detectOutlierLap(
  pairs: MetricValuePair[] | undefined,
  floor: number
): VarianceOutlierLap | undefined {
  if (!pairs) return undefined
  const values = pairs.map((p) => p.value)
  const result = detectSingleOutlier(values, floor)
  if (!result) return undefined
  const rest = values.filter((_, i) => i !== result.index)
  const restMedian = median(rest)
  return {
    lapNumber: pairs[result.index].lapNumber,
    delta: result.delta,
    restWithin: Math.max(...rest.map((v) => Math.abs(v - restMedian))),
    valueCount: values.length,
  }
}

function calculateZoneLength(zoneData: ZoneDataPoint[][]): number {
  if (!zoneData[0] || zoneData[0].length === 0) return 0
  const first = zoneData[0][0]?.distance ?? 0
  const last = zoneData[0][zoneData[0].length - 1]?.distance ?? 0
  return last - first
}

function calculateSpeedVariance(
  speeds: number[],
  noiseTolerance: NoiseToleranceConfig
) {
  const range = Math.max(...speeds) - Math.min(...speeds)
  return {
    stdDev: standardDeviation(speeds),
    range,
    isSignificant: range > noiseTolerance.minSpeedVariance,
  }
}

function calculateDistanceVariance(
  distances: number[],
  noiseTolerance: NoiseToleranceConfig
) {
  if (distances.length < 2) {
    return { stdDev: 0, range: 0, isSignificant: false }
  }
  const range = Math.max(...distances) - Math.min(...distances)
  return {
    stdDev: standardDeviation(distances),
    range,
    isSignificant: range > noiseTolerance.minDistanceVariance,
  }
}

function calculateApexLocationVariance(
  lapMetrics: LapZoneMetrics[],
  zoneLength: number,
  noiseTolerance: NoiseToleranceConfig
) {
  const apexDistances = lapMetrics.map((m) => m.apexDistance)
  const range = Math.max(...apexDistances) - Math.min(...apexDistances)
  return {
    stdDev: standardDeviation(apexDistances),
    range,
    avgApexPercent:
      zoneLength > 0 ? (average(apexDistances) / zoneLength) * 100 : 50,
    isSignificant: range > noiseTolerance.minDistanceVariance,
  }
}

function calculateThrottlePointVariance(
  lapMetrics: LapZoneMetrics[],
  zoneLength: number,
  noiseTolerance: NoiseToleranceConfig
) {
  const throttlePoints = lapMetrics
    .map((m) => m.throttlePointDistance)
    .filter((d): d is number => d !== null)
  if (throttlePoints.length < 2) {
    return { stdDev: 0, range: 0, avgThrottlePercent: 50, isSignificant: false }
  }
  const range = Math.max(...throttlePoints) - Math.min(...throttlePoints)
  return {
    stdDev: standardDeviation(throttlePoints),
    range,
    avgThrottlePercent:
      zoneLength > 0 ? (average(throttlePoints) / zoneLength) * 100 : 50,
    isSignificant: range > noiseTolerance.minDistanceVariance,
  }
}

function calculateGForceVariance(
  gForces: number[],
  noiseTolerance: NoiseToleranceConfig
) {
  const range = Math.max(...gForces) - Math.min(...gForces)
  return {
    stdDev: standardDeviation(gForces),
    range,
    avgPeakG: average(gForces),
    isSignificant: range > noiseTolerance.minGVariance,
  }
}

function calculateCoastingVariance(
  coastingDistances: number[],
  noiseTolerance: NoiseToleranceConfig
) {
  const range = Math.max(...coastingDistances) - Math.min(...coastingDistances)
  return {
    distanceStdDev: standardDeviation(coastingDistances),
    distanceRange: range,
    avgDistance: average(coastingDistances),
    isSignificant: range > noiseTolerance.minDistanceVariance,
  }
}

// ============================================================================
// Issue Prioritization
// ============================================================================

function determinePrimaryIssue(
  details: ZoneVarianceDetails,
  zoneType?: TrackZoneType
): InconsistencyType {
  const issues: { type: InconsistencyType; priority: number }[] = []

  if (details.entrySpeedVariance.isSignificant) {
    issues.push({
      type: 'entry-speed',
      priority: zoneType === 'braking' ? 100 : 60,
    })
  }
  if (details.brakingPointVariance.isSignificant) {
    issues.push({
      type: 'braking-point',
      priority: zoneType === 'braking' ? 95 : 40,
    })
  }
  if (details.brakingIntensityVariance.isSignificant) {
    issues.push({
      type: 'braking-intensity',
      priority: zoneType === 'braking' ? 80 : 30,
    })
  }
  if (details.apexLocationVariance.isSignificant) {
    issues.push({
      type: 'apex-location',
      priority: zoneType === 'cornering' ? 90 : 70,
    })
  }
  if (details.apexSpeedVariance.isSignificant) {
    issues.push({
      type: 'apex-speed',
      priority: zoneType === 'cornering' ? 85 : 65,
    })
  }
  if (details.throttlePointVariance.isSignificant) {
    issues.push({
      type: 'throttle-point',
      priority:
        zoneType === 'acceleration' || zoneType === 'cornering' ? 85 : 50,
    })
  }
  if (details.throttleIntensityVariance.isSignificant) {
    issues.push({
      type: 'throttle-intensity',
      priority: zoneType === 'acceleration' ? 80 : 40,
    })
  }
  if (details.coastingVariance.isSignificant) {
    issues.push({ type: 'coasting-duration', priority: 55 })
  }
  if (details.exitSpeedVariance.isSignificant) {
    issues.push({
      type: 'exit-speed',
      priority: zoneType === 'acceleration' ? 90 : 50,
    })
  }

  if (issues.length === 0) return null

  issues.sort((a, b) => b.priority - a.priority)
  return issues[0].type
}

function mapToLegacyInconsistency(
  primaryIssue: InconsistencyType
): 'speed' | 'line' | 'timing' | 'technique' | null {
  if (!primaryIssue) return null

  const speedIssues: InconsistencyType[] = [
    'entry-speed',
    'exit-speed',
    'apex-speed',
  ]
  const timingIssues: InconsistencyType[] = ['braking-point', 'throttle-point']
  const techniqueIssues: InconsistencyType[] = [
    'braking-intensity',
    'throttle-intensity',
    'coasting-duration',
  ]

  if (speedIssues.includes(primaryIssue)) return 'speed'
  if (primaryIssue === 'apex-location') return 'line'
  if (timingIssues.includes(primaryIssue)) return 'timing'
  if (techniqueIssues.includes(primaryIssue)) return 'technique'

  return null
}
